/**
 * Sticky Submenu JavaScript
 * Handles sticky behavior detection and section tracking
 */

class StickySubmenu {
  constructor(element) {
    this.element = element;
    this.links = element.querySelectorAll('.sticky-submenu__link');
    this.sections = [];
    this.currentActive = null;
    this.sectionObserver = null;
    this.isSticky = false;
    this.originalTop = 0;
    this.placeholder = null;
    this.lastScrollTop = 0;
    this.isInitialized = false;
    this.chevron = element.querySelector('.sticky-submenu__chevron');
    this.list = element.querySelector('.sticky-submenu__list');

    this.init();
  }

  init() {
    this.setupSectionTracking();
    this.bindEvents();
    this.createPlaceholder();
    this.setupScrollListener();
    this.handleBrowserNavigation();
    this.setupChevronVisibility();
  }

  createPlaceholder() {
    // Create a placeholder element to maintain layout when element becomes fixed
    this.placeholder = document.createElement('div');
    this.placeholder.style.display = 'none';
    this.placeholder.style.height = this.element.offsetHeight + 'px';
    this.element.parentNode.insertBefore(this.placeholder, this.element.nextSibling);
  }

  setupScrollListener() {
    // Wait for complete layout stability before initializing
    const initializeWhenReady = () => {
      if (document.readyState === 'complete') {
        // Additional wait for any dynamic content and layout shifts
        setTimeout(() => {
          this.calculateOriginalPosition();
          this.setupScrollHandling();
        }, 150);
      } else {
        window.addEventListener('load', () => {
          setTimeout(() => {
            this.calculateOriginalPosition();
            this.setupScrollHandling();
          }, 150);
        });
      }
    };

    initializeWhenReady();
  }

  setupScrollHandling() {
    // Only start listening after position is properly calculated
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.checkStickyPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    this.scrollHandler = handleScroll;

    // Mark as initialized and do initial check
    this.isInitialized = true;
    this.checkStickyPosition();
  }

  calculateOriginalPosition() {
    // Use offsetTop which gives position relative to document regardless of scroll
    // This is more reliable than getBoundingClientRect when page loads scrolled
    this.originalTop = this.element.offsetTop;

    // Fallback method if offsetTop seems incorrect (e.g., element not in normal flow)
    if (this.originalTop <= 0) {
      const currentScroll = window.scrollY || document.documentElement.scrollTop;

      // Temporarily scroll to top to get accurate position
      window.scrollTo(0, 0);
      const rect = this.element.getBoundingClientRect();
      this.originalTop = rect.top;

      // Restore original scroll position
      window.scrollTo(0, currentScroll);
    }
  }

  checkStickyPosition() {
    // Don't proceed if we haven't properly initialized yet
    if (!this.isInitialized || this.originalTop === 0) {
      return;
    }

    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    // Add some buffer to ensure we don't stick too early
    const buffer = 10;
    const shouldBeSticky = scrollTop >= (this.originalTop - buffer);

    // Detect scroll direction
    const scrollDirection = scrollTop > this.lastScrollTop ? 'down' : 'up';
    this.lastScrollTop = scrollTop;

    if (shouldBeSticky && !this.isSticky) {
      this.makeSticky(scrollDirection);
    } else if (!shouldBeSticky && this.isSticky) {
      this.removeSticky();
    } else if (shouldBeSticky && this.isSticky) {
      // Update sticky position based on scroll direction
      this.updateStickyPosition(scrollDirection);
    }
  }

  makeSticky(scrollDirection = 'down') {
    this.isSticky = true;
    this.element.style.position = 'fixed';
    this.element.style.left = '0';
    this.element.style.right = '0';
    this.element.style.width = '100%';

    // Set top position based on scroll direction and screen size
    this.updateStickyPosition(scrollDirection);

    this.element.classList.add('sticky-submenu--stuck');

    // Show placeholder to maintain layout
    this.placeholder.style.display = 'block';

    // Dispatch custom event
    this.element.dispatchEvent(new CustomEvent('stickyStateChange', {
      detail: { isSticky: true, scrollDirection: scrollDirection }
    }));
  }

  updateStickyPosition(scrollDirection) {
    if (!this.isSticky) return;

    // Get screen size to determine header heights (matching your dynamic tabbar)
    const isMobile = window.innerWidth < 768;

    let topPosition;

    // Check if header is actually visible
    const headerSection = document.querySelector('.shopify-section--header');
    const headerIsVisible = headerSection && getComputedStyle(headerSection).visibility !== 'hidden';

    if (scrollDirection === 'up' || headerIsVisible) {
      // Header is visible - position below header and banner (if banner is visible)
      // Get header height from CSS variable or calculate dynamically
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) ||
                          (isMobile ? 82 : 96);

      // Only check for banner if we're near the top of the page
      // When scrolling down past the sticky point, banner has likely scrolled away
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const bannerThreshold = 200; // Only include banner if within 200px of top

      let bannerHeight = 0;
      if (scrollTop < bannerThreshold) {
        // Check if banner is currently visible above the viewport
        const announcementBanner = document.querySelector('.anBarWrapper');
        const countdownTimerBanner = document.querySelector('.timer__Container');
        const bannerElement = countdownTimerBanner || announcementBanner;

        if (bannerElement) {
          const bannerRect = bannerElement.getBoundingClientRect();
          // Only include banner height if it's still visible (hasn't scrolled completely past)
          if (bannerRect.bottom > 0) {
            bannerHeight = bannerElement.offsetHeight;
          }
        }
      }

      // Position below header + banner (only if banner is visible)
      //Use this when header is sticky
      // topPosition = (headerHeight + bannerHeight) + 'px';
      
      //Use this when header is not sticky
      topPosition = '0px';
    } else {
      // Header is hidden - stick to top
      topPosition = '0';
    }

    this.element.style.top = topPosition;
  }

  removeSticky() {
    this.isSticky = false;
    this.element.style.position = '';
    this.element.style.top = '';
    this.element.style.left = '';
    this.element.style.right = '';
    this.element.style.width = '';
    this.element.classList.remove('sticky-submenu--stuck');

    // Hide placeholder
    this.placeholder.style.display = 'none';

    // Dispatch custom event
    this.element.dispatchEvent(new CustomEvent('stickyStateChange', {
      detail: { isSticky: false }
    }));
  }

  setupSectionTracking() {
    // Find all sections that correspond to menu items
    this.links.forEach(link => {
      const targetId = link.getAttribute('data-section-target');
      if (targetId) {
        const section = document.getElementById(targetId);
        if (section) {
          this.sections.push({
            element: section,
            link: link,
            id: targetId
          });
        }
      }
    });

    // Set up intersection observer for section tracking
    if (this.sections.length > 0) {
      this.setupIntersectionObserver();
    }
  }

  setupIntersectionObserver() {
    // Simple scroll-based detection only
    this.addScrollBasedDetection();
  }

  addScrollBasedDetection() {
    // Very basic scroll-based detection - only highlight one section at a time
    const checkActiveSection = () => {
      if (!this.sections.length) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const stickyHeight = this.isSticky ? this.element.offsetHeight : 0;
      const offset = stickyHeight + 100; // Simple offset from top

      let activeSection = null;

      // Check if we're before the first section (in hero section)
      // If so, highlight the first menu item
      if (this.sections.length > 0) {
        const firstSectionTop = this.sections[0].element.offsetTop;

        if (scrollTop + offset < firstSectionTop) {
          // We're in the hero section, highlight the first menu item
          activeSection = this.sections[0];
        } else {
          // Find the section that is currently visible from the top
          for (let i = 0; i < this.sections.length; i++) {
            const sectionData = this.sections[i];
            const sectionTop = sectionData.element.offsetTop;
            const sectionBottom = sectionTop + sectionData.element.offsetHeight;

            // Check if we've scrolled past this section's start
            if (scrollTop + offset >= sectionTop && scrollTop + offset < sectionBottom) {
              activeSection = sectionData;
              break;
            }

            // If we're past all sections, highlight the last one
            if (i === this.sections.length - 1 && scrollTop + offset >= sectionTop) {
              activeSection = sectionData;
            }
          }
        }
      }

      // Only update if we have a different active section
      if (activeSection && activeSection.link !== this.currentActive) {
        this.setActiveSection(activeSection.link);
      }
    };

    // Throttle the scroll-based detection
    let scrollTicking = false;
    const handleScrollDetection = () => {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          checkActiveSection();
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    };

    window.addEventListener('scroll', handleScrollDetection);

    // Initial check to set the first item as active when page loads
    setTimeout(() => {
      checkActiveSection();
    }, 200);

    // Store reference for cleanup
    this.scrollDetectionHandler = handleScrollDetection;
  }

  setActiveSection(activeLink) {
    if (this.currentActive === activeLink) return;

    // Remove active class from all links - simple highlighting
    this.links.forEach(link => {
      link.classList.remove('sticky-submenu__link--active');
    });

    // Add active class to current link - simple highlighting
    if (activeLink) {
      activeLink.classList.add('sticky-submenu__link--active');
      this.currentActive = activeLink;

      // Ensure the active item is visible in the scrollable area
      this.scrollActiveItemIntoView(activeLink);

      // Dispatch custom event for active section change
      this.element.dispatchEvent(new CustomEvent('activeSectionChange', {
        detail: {
          activeLink: activeLink,
          sectionId: activeLink.getAttribute('data-section-target')
        }
      }));
    }
  }

  // Helper method to ensure active item is visible in horizontal scroll
  scrollActiveItemIntoView(activeLink) {
    if (!activeLink) return;

    if (!this.list) return;

    const listRect = this.list.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();

    // Check if the active link is outside the visible area
    if (linkRect.left < listRect.left || linkRect.right > listRect.right) {
      // Calculate scroll position to center the active link
      const scrollLeft = this.list.scrollLeft + (linkRect.left - listRect.left) -
        (listRect.width / 2) + (linkRect.width / 2);

      // Smooth scroll to the position
      this.list.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }

    // Update chevron visibility after scrolling
    setTimeout(() => {
      this.updateChevronVisibility();
    }, 300);
  }

  bindEvents() {
    // Smooth scroll when clicking menu items
    this.links.forEach(link => {
      link.addEventListener('click', (e) => {
        const targetId = link.getAttribute('data-section-target');
        if (targetId) {
          e.preventDefault();
          const targetSection = document.getElementById(targetId);
          if (targetSection) {
            const stickyHeight = this.isSticky ? this.element.offsetHeight : 0;
            const offsetTop = targetSection.offsetTop - stickyHeight - 20;
            window.scrollTo({
              top: offsetTop,
              behavior: 'smooth'
            });

            // Immediately reflect active state on click for better UX
            this.setActiveSection(link);
          }
        }
      });
    });

    // Smooth scroll when clicking CTA button
    const ctaButton = this.element.querySelector('.sticky-submenu__button');
    if (ctaButton) {
      ctaButton.addEventListener('click', (e) => {
        const href = ctaButton.getAttribute('href');
        const fallbackUrl = ctaButton.getAttribute('data-fallback-url');
        // Check if it's an anchor link (starts with #)
        if (href && href.startsWith('#')) {
          const targetId = href.substring(1); // Remove the # symbol
          const targetSection = document.getElementById(targetId) ?? document.getElementById(fallbackUrl.substring(1));

          if (targetSection) {
            e.preventDefault();
            const stickyHeight = this.isSticky ? this.element.offsetHeight : 0;
            const offsetTop = targetSection.offsetTop - stickyHeight - 20;
            window.scrollTo({
              top: offsetTop,
              behavior: 'smooth'
            });
          }
        }
        // If it's not an anchor link, allow normal navigation
      });
    }

    // Recalculate position on window resize
    window.addEventListener('resize', () => {
      if (this.placeholder) {
        this.placeholder.style.height = this.element.offsetHeight + 'px';
      }
      // Recalculate original position after resize
      setTimeout(() => {
        this.calculateOriginalPosition();
        this.checkStickyPosition();
        this.updateChevronVisibility();
      }, 100);
    });
  }

  // Public method to manually set active section
  setActiveSectionById(sectionId) {
    const targetLink = Array.from(this.links).find(link =>
      link.getAttribute('data-section-target') === sectionId
    );
    if (targetLink) {
      this.setActiveSection(targetLink);
    }
  }

  // Setup chevron visibility logic
  setupChevronVisibility() {
    if (!this.chevron || !this.list) return;

    // Initial check with multiple attempts to ensure layout is settled
    const checkVisibility = () => {
      this.updateChevronVisibility();
    };

    // Check immediately
    checkVisibility();

    // Check after short delay
    setTimeout(checkVisibility, 100);

    // Check after longer delay to ensure all layout is complete
    setTimeout(checkVisibility, 300);

    // Listen for scroll events on the list to update chevron
    this.list.addEventListener('scroll', () => {
      this.updateChevronVisibility();
    });

    // Also listen for resize events to update chevron visibility
    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.updateChevronVisibility();
      }, 100);
    });

    // Add click handler to chevron for scrolling
    this.chevron.addEventListener('click', () => {
      this.scrollMenuRight();
    });
  }

  // Scroll the menu to the right when chevron is clicked
  scrollMenuRight() {
    if (!this.list) return;

    const scrollAmount = this.list.clientWidth * 0.7; // Scroll 70% of visible width
    const newScrollLeft = this.list.scrollLeft + scrollAmount;

    this.list.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });

    // Update chevron visibility after scrolling
    setTimeout(() => {
      this.updateChevronVisibility();
    }, 300);
  }

  // Update chevron visibility based on scroll state
  updateChevronVisibility() {
    if (!this.chevron || !this.list) return;

    // Force a reflow to ensure accurate measurements
    this.list.offsetWidth;

    const scrollLeft = this.list.scrollLeft;
    const clientWidth = this.list.clientWidth;
    const scrollWidth = this.list.scrollWidth;

    // Get the chevron's width to account for overlap
    const chevronWidth = 56; // Fixed width as defined in CSS

    // Check if there's content that can be scrolled to the right
    // We need to account for the chevron's width to prevent overlap
    const buffer = 16; // Buffer for better spacing and fade effect
    const availableSpace = scrollLeft + clientWidth;
    const totalContentWidth = scrollWidth;

    // Calculate how much content is hidden to the right
    const hiddenContentWidth = totalContentWidth - availableSpace;

    // Only show chevron if there's enough hidden content to justify it
    // and if showing it won't cause overlap
    const hasSignificantHiddenContent = hiddenContentWidth > chevronWidth + buffer;
    const canScrollRight = availableSpace < totalContentWidth - buffer;

    // Debug logging (uncomment for debugging)
    // console.log('Chevron visibility check:', {
    //   scrollLeft,
    //   clientWidth,
    //   scrollWidth,
    //   chevronWidth,
    //   availableSpace,
    //   totalContentWidth,
    //   hiddenContentWidth,
    //   canScrollRight,
    //   hasSignificantHiddenContent,
    //   shouldShow: canScrollRight && hasSignificantHiddenContent
    // });

    if (canScrollRight && hasSignificantHiddenContent) {
      this.chevron.style.display = 'flex';
    } else {
      this.chevron.style.display = 'none';
    }
  }

  // Handle browser back/forward navigation and page cache
  handleBrowserNavigation() {
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        // Page was loaded from browser cache, reinitialize
        this.isInitialized = false;
        setTimeout(() => {
          this.calculateOriginalPosition();
          this.setupScrollHandling();
          this.updateChevronVisibility();
        }, 100);
      }
    });

    // Handle browser back/forward buttons
    window.addEventListener('popstate', () => {
      setTimeout(() => {
        this.calculateOriginalPosition();
        this.checkStickyPosition();
        this.updateChevronVisibility();
      }, 100);
    });
  }

  // Public method to destroy the instance and clean up observers
  destroy() {
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.scrollDetectionHandler) {
      window.removeEventListener('scroll', this.scrollDetectionHandler);
    }
    if (this.placeholder && this.placeholder.parentNode) {
      this.placeholder.parentNode.removeChild(this.placeholder);
    }
    this.removeSticky();
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  const stickySubmenus = document.querySelectorAll('[data-sticky-submenu]');

  stickySubmenus.forEach(submenu => {
    // Store instance on element for potential external access
    submenu.stickySubmenuInstance = new StickySubmenu(submenu);
  });
});

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = StickySubmenu;
}
